/** Iter 232 · Workspace-level PIP configuration.
 *
 *  Owner/admin only. Lets HR toggle which signals trigger a PIP, set the
 *  threshold for each, and configure default plan duration + consequences.
 *
 *  All signals start DISABLED (per user request). Enabling one doesn't
 *  auto-initiate PIPs — Phase 3 adds a nightly cron that flags at-risk
 *  employees. For now the config is what surfaces on employee dashboards
 *  as the "here's what could trigger a PIP" placeholder.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Save, Loader2, ShieldAlert, Info, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { apiErrorMessage } from "@/lib/apiError";

const RATING_HINT = "Enabling a signal makes it appear on every employee's My Performance card as a live trigger. Disabled signals still show but are labelled 'not currently enforced'.";

export default function PIPSettingsSection() {
  const { activeWorkspaceId, activeWorkspace, user } = useAuth();
  const role = activeWorkspace?.role || activeWorkspace?.membership_role;
  const isAdmin = user?.is_super_admin || role === "owner" || role === "admin";

  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consequenceInput, setConsequenceInput] = useState("");

  const load = async () => {
    if (!activeWorkspaceId || !isAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/settings/pip`);
      setCfg(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load PIP settings"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, isAdmin]);

  const patchRule = (key, patch) => setCfg((c) => ({
    ...c,
    rules: c.rules.map((r) => r.key === key ? { ...r, ...patch } : r),
  }));

  const addConsequence = () => {
    const v = consequenceInput.trim().replace(/\s+/g, "_").toLowerCase();
    if (!v) return;
    if ((cfg.consequences || []).includes(v)) { toast.error("Already added"); return; }
    setCfg((c) => ({ ...c, consequences: [...(c.consequences || []), v] }));
    setConsequenceInput("");
  };
  const removeConsequence = (v) => setCfg((c) => ({
    ...c, consequences: (c.consequences || []).filter((x) => x !== v),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/workspaces/${activeWorkspaceId}/settings/pip`, {
        rules: cfg.rules,
        default_duration_days: cfg.default_duration_days,
        default_checkpoint_days: cfg.default_checkpoint_days,
        consequences: cfg.consequences,
      });
      setCfg(data);
      toast.success("PIP settings saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  if (!isAdmin) {
    return (
      <div className="border border-border p-6 text-sm text-muted-foreground" data-testid="settings-pip-forbidden">
        Only workspace owners/admins can view or change PIP settings.
      </div>
    );
  }
  if (loading || !cfg) return <div className="text-sm text-muted-foreground p-6">Loading…</div>;

  const meta = cfg.signal_meta || {};

  return (
    <div className="space-y-6" data-testid="settings-pip-section">
      <div className="border border-border p-6 space-y-4">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-primary" />
          <div className="label-eyebrow">Eligibility signals</div>
        </div>
        <p className="text-sm text-muted-foreground -mt-2 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          {RATING_HINT}
        </p>
        <div className="divide-y divide-border border border-border">
          {cfg.rules.map((r) => {
            const m = meta[r.key] || { label: r.key, unit: "", hint: "" };
            return (
              <div key={r.key} className="p-4 flex items-start gap-4" data-testid={`pip-rule-${r.key}`}>
                <div className="pt-1">
                  <Switch
                    checked={r.enabled}
                    onCheckedChange={(v) => patchRule(r.key, { enabled: !!v })}
                    data-testid={`pip-rule-${r.key}-toggle`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{m.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{m.hint}</div>
                  {r.enabled && (
                    <label className="mt-2 inline-flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer">
                      <input type="checkbox" checked={!!r.surface_only}
                                onChange={(e) => patchRule(r.key, { surface_only: e.target.checked })}
                                data-testid={`pip-rule-${r.key}-surface-only`} />
                      Surface but don&apos;t notify (preview mode — flags appear on admin page only)
                    </label>
                  )}
                </div>
                <div className="w-40 shrink-0">
                  <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">
                    Threshold ({m.unit || "value"})
                  </label>
                  <Input
                    type="number" min="0" step="1"
                    value={r.threshold}
                    onChange={(e) => patchRule(r.key, { threshold: Number(e.target.value) })}
                    className="h-9 font-mono"
                    data-testid={`pip-rule-${r.key}-threshold`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border border-border p-6 space-y-4">
        <div className="label-eyebrow">Defaults for new plans</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Plan duration (days)</label>
            <Input
              type="number" min="7" max="180"
              value={cfg.default_duration_days || 60}
              onChange={(e) => setCfg((c) => ({ ...c, default_duration_days: Number(e.target.value) }))}
              className="h-10 font-mono"
              data-testid="pip-default-duration"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">Checkpoint cadence (days)</label>
            <Input
              type="number" min="1" max="90"
              value={cfg.default_checkpoint_days || 14}
              onChange={(e) => setCfg((c) => ({ ...c, default_checkpoint_days: Number(e.target.value) }))}
              className="h-10 font-mono"
              data-testid="pip-default-checkpoint"
            />
          </div>
        </div>
      </div>

      <div className="border border-border p-6 space-y-4">
        <div className="label-eyebrow">Consequences on failure</div>
        <p className="text-sm text-muted-foreground -mt-2">
          These are the outcomes admins can pick from when closing a failed PIP. Keep them factual — no scary language.
        </p>
        <div className="flex flex-wrap gap-2">
          {(cfg.consequences || []).map((v) => (
            <span key={v} className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-muted/40 text-xs" data-testid={`pip-consequence-${v}`}>
              {v.replace(/_/g, " ")}
              <button type="button" onClick={() => removeConsequence(v)}
                        className="text-muted-foreground hover:text-rose-600"
                        data-testid={`pip-consequence-${v}-remove`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={consequenceInput}
            onChange={(e) => setConsequenceInput(e.target.value)}
            placeholder="e.g. formal_warning"
            className="h-9 flex-1"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addConsequence(); } }}
            data-testid="pip-consequence-input"
          />
          <Button type="button" onClick={addConsequence} className="h-9 rounded-none" variant="outline" data-testid="pip-consequence-add">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving} className="h-10 rounded-none" data-testid="pip-settings-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save PIP settings
        </Button>
      </div>
    </div>
  );
}
